<template>
  <div
    :key="uniqueKey" class="rich-text"
    :style="richRootInlineStyle">
    <div
      id="editable"
      ref="editable"
      class="rich-text-block"
      :style="richTextEditableStyle">
    </div>
    <div
      v-if="options.disableEditing || isIndicatorReadonly"
      class="editable-mask"
      @dblclick="handleDoubleClick" />
  </div>
</template>

<script>
import MediumEditor from 'medium-editor'
import ChartMixin from '@/components/charts/mixin'
import { getFontFamilyCss } from '@/utils/chartTitlePresets'

export default {
  name: 'RichTextBlock',
  mixins: [ChartMixin],
  props: {
    config: {
      type: Object,
    },
    globalConfig: {
      type: Object,
    },
    componentType: {
      type: String,
    },
    filterParams: {
      type: Object,
      default: () => ({}),
    },
  },
  data () {
    return {
      disableEdit: true,
      uniqueKey: 'xxx',
      options: {
        toolbar: false,
        disableEditing: true,
        anchor: {
          placeholderText: 'Paste or type a link',
          customClassOption: 'btn',
          customClassOptionText: 'Create Button',
        },
        placeholder: {
          text: '',
          hideOnClick: false,
        },
      },
      editor: null,
    }
  },
  computed: {
    /** 内联 font-family 覆盖 medium-editor 对 .medium-editor-element 的全局字体 */
    richTextEditableStyle () {
      const t = this.config?.chartSet?.titleStyle
      const ff = getFontFamilyCss(t?.fontFamily)
      return ff ? { fontFamily: ff } : {}
    },
    richRootInlineStyle () {
      return {
        width: this.config.chartWidth,
        height: this.config.chartHeight,
        fontSize: '22px',
      }
    },
    isActive () {
      return this.config.active
    },
    /** 已选指标时由接口驱动正文，禁止画布内直接编辑 */
    isIndicatorReadonly () {
      return this.dataSource === 'indicatorLib' && this.selectedIndicatorIdList?.length > 0
    },
    fontColor () {
      return this.config.chartSet.titleStyle.color
    },

    bold () {
      return this.config.chartSet.titleStyle.fontStyle.bold
    },
    italic () {
      return this.config.chartSet.titleStyle.fontStyle.italic
    },
    underline () {
      return this.config.chartSet.titleStyle.fontStyle.underline
    },
    strikeThrough () {
      return this.config.chartSet.titleStyle.fontStyle.strikeThrough
    },
    color () {
      return this.config.chartSet.titleStyle.fontStyle.color
    },
    fontSize () {
      return this.config.chartSet.titleStyle.fontStyle.fontSize
    },

    fontAlign () {
      return this.config.chartSet.titleStyle.align
    },
  },
  watch: {
    'config.chartViewData.chartData': {
      handler (val) {
        if (!this.$refs.editable) return
        const next = val == null ? '' : String(val)
        const cur = this.editor?.serialize?.()?.editable?.value
        if (cur !== undefined && cur === next) return
        this.$refs.editable.innerHTML = next
        if (this.editor) {
          this.editor.destroy()
          this.editor = null
        }
        this.options = { ...this.options, ...this.getEditorOptions() }
        this.editor = new MediumEditor(this.$refs.editable, this.options)
        this.bindEditorBlur()
      },
    },
    isActive (val) {
      if (!val) {
        this.config.draggable = true
      }
      this.options.disableEditing = true
      this.options = { ...this.options, ...this.getEditorOptions() }
      this.updateEditor()
      if (this.editor && !this.isIndicatorReadonly) {
        this.config.chartViewData.chartData = this.editor.serialize().editable.value
      }
    },
    fontColor: {
      handler (val) {
        this.handleChangeStyle('ForeColor', this.fontColor)
      },
    },

    fontSize: {
      handler (val) {
        this.handleChangeStyle('FontSize', this.config.chartSet.titleStyle.fontSize)
      },
    },
    bold (val, oldVal) {
      this.handleChangeStyle('bold')
    },
    italic (val, oldVal) {
      this.handleChangeStyle('italic')
    },
    underline (val, oldVal) {
      this.handleChangeStyle('underline')
    },
    strikeThrough (val, oldVal) {
      this.handleChangeStyle('strikeThrough')
    },
    color (val, oldVal) {
      this.handleChangeStyle('foreColor', this.config.chartSet.titleStyle.color)
      this.$nextTick(() => {
        if (this.editor && !this.isIndicatorReadonly) {
          this.config.chartViewData.chartData = this.editor.serialize().editable.value
        }
      })
    },

    fontAlign: {
      handler (newValue, oldValue) {
        const map = {
          left: 'JustifyLeft',
          center: 'JustifyCenter',
          right: 'JustifyRight',
        }
        this.handleChangeStyle(map[this.fontAlign])
      },

    },
  },
  created () {
    this.uniqueKey = Math.random()
    const ts = this.config?.chartSet?.titleStyle
    if (ts && ts.fontFamily === undefined) this.$set(ts, 'fontFamily', '')
  },

  mounted () {
    this.options = { ...this.options, ...this.getEditorOptions() }
    this.editor = new MediumEditor(this.$refs.editable, this.options)
    this.$refs.editable.innerHTML = this.config.chartViewData.chartData || ''
    this.bindEditorBlur()
  },
  methods: {
    getEditorOptions () {
      const hasContent = !!(this.config.chartViewData?.chartData)
      return {
        toolbar: false,
        // 勿在此写 disableEditing：updateEditor 会 merge 本对象，会覆盖双击后设为 false 的可编辑状态
        anchor: this.options.anchor,
        placeholder: {
          text: this.isIndicatorReadonly
            ? ''
            : (hasContent ? '' : '双击即可输入'),
          hideOnClick: false,
        },
      }
    },
    bindEditorBlur () {
      if (!this.editor) return
      this.editor.subscribe('editableBlur', () => {
        if (this.isIndicatorReadonly) return
        this.editor.saveSelection()
        this.editor.restoreSelection()
        this.$nextTick(() => {
          this.config.chartViewData.chartData = this.editor.serialize().editable.value
        })
      })
    },
    updateEditor () {
      if (this.editor) {
        this.editor.destroy()
        this.editor = null
      }
      this.options = { ...this.options, ...this.getEditorOptions() }
      this.editor = new MediumEditor(this.$refs.editable, this.options)
      this.bindEditorBlur()
    },

    handleDoubleClick () {
      if (this.isIndicatorReadonly) return
      this.options.disableEditing = false
      this.updateEditor()
      this.editor.origElements.focus()
      this.config.draggable = false
    },
    handleChangeStyle (item, style) {
      if (this.isIndicatorReadonly) return
      const selectContent = MediumEditor.selection.getSelectionHtml(this.editor.options.ownerDocument)
      const editorContent = this.editor.serialize().editable.value
      if (!selectContent && editorContent) {
        this.handleDoubleClick()
        this.editor.selectAllContents()
      }
      this.editor.options.ownerDocument.execCommand('styleWithCSS', null, true)
      this.editor.options.ownerDocument.execCommand(item, false, style)
    },
  },
}
</script>

<style lang="scss" scoped>
.rich-text {
  position: relative;
  width: 100%;
  height: 100%;
  .rich-text-block{
    cursor: default;
    width: 100%;
    height: 100%;
    overflow: hidden;
    padding: 5px;
    color: #fff;
    &:focus-visible {
      outline: none;
    }
  }
  .editable-mask{
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
  }

}

</style>
